import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./lib/auth";
import { ProjectsProvider } from "./lib/projects";
import ProtectedRoute from "./components/ProtectedRoute";
import WorkspaceShell from "./components/workspace/WorkspaceShell";
import RootLayout from "./components/layout/RootLayout";

import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import WorkspaceHomePage from "./pages/WorkspaceHomePage";
import DashboardHomePage from "./pages/DashboardHomePage";
import ProjectsPage from "./pages/ProjectsPage";
import StrategyMarketingPage from "./pages/StrategyMarketingPage";
import FundingPage from "./pages/FundingPage";
import TemplatesPage from "./pages/TemplatesPage";

import SettingsPage from "./pages/SettingsPage";
import NetworkConnectorPage from "./pages/NetworkConnectorPage";
import { CompanyDetailPage } from "./pages/DiscoverPages";
import DiscoverPortalPage from "./pages/discover/DiscoverPortalPage";
import StartupsPage from "./pages/discover/StartupsPage";
import IncubatorsPage from "./pages/discover/IncubatorsPage";
import VcFirmsPage from "./pages/discover/VcFirmsPage";
import TrendingStartupsPage from "./pages/discover/TrendingStartupsPage";
import ExploreIndustriesPage from "./pages/discover/ExploreIndustriesPage";
import StartupNewsPage from "./pages/discover/StartupNewsPage";
import BrowseStartupsPage from "./pages/discover/BrowseStartupsPage";
import StartupBlogPage from "./pages/discover/StartupBlogPage";
import FeaturedListsPage from "./pages/discover/FeaturedListsPage";
import HiringCompaniesPage from "./pages/discover/HiringCompaniesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";

import "./App.css";

function AppRouter() {
  const location = useLocation();
  const hasAuthCallback =
    (location.hash && (
      location.hash.includes("session_id=") ||
      location.hash.includes("access_token=") ||
      location.hash.includes("id_token=") ||
      location.hash.includes("error=") ||
      location.hash.includes("error_description=")
    )) ||
    (location.search && (
      location.search.includes("code=") ||
      location.search.includes("error=") ||
      location.search.includes("error_description=")
    ));

  if (hasAuthCallback) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LandingPage />} />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><WorkspaceHomePage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/dashboard"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><DashboardHomePage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/projects"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><ProjectsPage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/strategy-marketing"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><StrategyMarketingPage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route path="/workspace/strategy" element={<Navigate to="/workspace/strategy-marketing?tab=strategy" replace />} />
        <Route path="/workspace/marketing" element={<Navigate to="/workspace/strategy-marketing?tab=marketing" replace />} />
        <Route
          path="/workspace/funding"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><FundingPage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/templates"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><TemplatesPage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route path="/workspace/documents" element={<Navigate to="/workspace/templates" replace />} />
        <Route path="/documents" element={<Navigate to="/templates" replace />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/workspace/analytics" element={<Navigate to="/workspace/dashboard" replace />} />
        <Route
          path="/workspace/settings"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><SettingsPage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/network-connector"
          element={
            <ProtectedRoute>
              <ProjectsProvider>
                <WorkspaceShell><NetworkConnectorPage /></WorkspaceShell>
              </ProjectsProvider>
            </ProtectedRoute>
          }
        />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
        <Route path="/discover" element={<DiscoverPortalPage />} />
        <Route path="/discover/startups" element={<StartupsPage />} />
        <Route path="/discover/incubators" element={<IncubatorsPage />} />
        <Route path="/discover/vc-firms" element={<VcFirmsPage />} />
        <Route path="/discover/trending-startups" element={<TrendingStartupsPage />} />
        <Route path="/discover/industries" element={<ExploreIndustriesPage />} />
        <Route path="/discover/news" element={<StartupNewsPage />} />
        <Route path="/discover/browse" element={<BrowseStartupsPage />} />
        <Route path="/discover/blog" element={<StartupBlogPage />} />
        <Route path="/discover/featured" element={<FeaturedListsPage />} />
        <Route path="/discover/hiring" element={<HiringCompaniesPage />} />
        <Route path="/discover/company/:slug" element={<CompanyDetailPage />} />
        
        {/* Redirect old routes */}
        <Route path="/startups" element={<Navigate to="/discover/startups" replace />} />
        <Route path="/incubators" element={<Navigate to="/discover/incubators" replace />} />
        <Route path="/vc-firms" element={<Navigate to="/discover/vc-firms" replace />} />
        
        {/* Legacy and helper redirects */}
        <Route path="/discover/lists" element={<Navigate to="/discover/featured" replace />} />
        <Route path="/discover/remote" element={<Navigate to="/discover/hiring" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
